import React from 'react';
import {
  PiCheckCircleLight,
  PiInfoLight,
  PiWarningLight,
} from 'react-icons/pi';
import { Text } from 'src/modules/shared/components/atoms/text';
import type { TextColor } from 'src/modules/shared/components/atoms/text/text.styles';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';
import type {
  WebsiteLogoFinding,
  WebsiteLogoSize,
} from '../../utils/website-logo-guidance.util';

/** Icon, text color and message of each kind of finding. */
const FINDING_TONES: Record<
  WebsiteLogoFinding['kind'],
  { color: TextColor; Icon: typeof PiInfoLight; messageKey: TranslationKey }
> = {
  ideal: {
    color: 'success',
    Icon: PiCheckCircleLight,
    messageKey: 'websites.settings.logo.check.ideal',
  },
  square: {
    color: 'secondary',
    Icon: PiInfoLight,
    messageKey: 'websites.settings.logo.check.square',
  },
  small: {
    color: 'warning',
    Icon: PiWarningLight,
    messageKey: 'websites.settings.logo.check.small',
  },
};

/** Props for the logo findings. */
interface WebsiteLogoFindingsProps {
  /** Natural size of the current logo. */
  size: WebsiteLogoSize;
  /** Findings about the current logo, most important first. */
  findings: WebsiteLogoFinding[];
}

/**
 * Tells the agency how its uploaded logo will look in the designs: sharp and
 * well shaped, shown beside the agency name, or too small to stay sharp.
 *
 * @param props - Logo size and findings.
 * @returns One line per finding.
 */
export const WebsiteLogoFindings: React.FC<WebsiteLogoFindingsProps> = ({
  size,
  findings,
}) => {
  const { t } = useI18n();

  return (
    <div className="space-y-1">
      {findings.map((finding) => {
        const { color, Icon, messageKey } = FINDING_TONES[finding.kind];
        const message =
          finding.kind === 'small'
            ? t(messageKey, {
                width: size.width,
                height: size.height,
                minWidth: finding.minWidth,
                minHeight: finding.minHeight,
              })
            : t(messageKey);

        return (
          <Text
            key={finding.kind}
            size="small"
            color={color}
            className="flex items-start gap-1.5"
          >
            <Icon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{message}</span>
          </Text>
        );
      })}
    </div>
  );
};
